import * as React from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'

import { useInteractionMotion } from '@/components/client/third-party/framer-motion/use-lazy-framer-motion'
import { inter } from '@/fonts'
import SvgXmark from '@/icons/thin/Xmark'
import { tw } from '@/lib/utils'

export interface ModalProps extends React.PropsWithChildren {
  isOpen: boolean
  title?: string
  onOpen?: () => void
  onClose: () => void
  overlayBackgroundColor?: string
  contentBackgroundColor?: string
  classes?: ModalClasses
  renderOutsideContent?: React.ReactNode
}

interface ModalClasses {
  overlay?: string
  frame?: string
  panelContainer?: string
  panel?: string
}

const Root = ({
  isOpen,
  title = 'Dialog',
  onOpen,
  onClose,
  children,
  classes,
  overlayBackgroundColor,
  contentBackgroundColor,
  renderOutsideContent,
}: ModalProps) => {
  const { motion } = useInteractionMotion()

  // Capture motion availability when modal opens to prevent element type changes mid-session
  const motionCapturedRef = React.useRef(false)
  const motionWhenOpenedRef = React.useRef<typeof motion>(null)

  if (isOpen && !motionCapturedRef.current) {
    // Capture on first render when opening (not in effect, to avoid flicker)
    motionWhenOpenedRef.current = motion
    motionCapturedRef.current = true
  } else if (!isOpen && motionCapturedRef.current) {
    // Reset when closed
    motionWhenOpenedRef.current = null
    motionCapturedRef.current = false
  }

  // Use the motion that was captured when opened
  const stableMotion = motionCapturedRef.current ? motionWhenOpenedRef.current : motion
  const MotionDiv = stableMotion?.div || 'div'

  React.useEffect(() => {
    if (isOpen && onOpen) {
      // Small delay to match the animation completion timing
      const timer = setTimeout(onOpen, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen, onOpen])

  const renderContentWithAnimation = () => (
    <DialogPrimitive.Portal forceMount>
      <DialogPrimitive.Overlay asChild forceMount>
        <div
          className={tw('bg-overlay fixed inset-0 z-[1000]', classes?.overlay)}
          style={overlayBackgroundColor ? { backgroundColor: overlayBackgroundColor } : {}}
        />
      </DialogPrimitive.Overlay>
      <div className={tw('fixed inset-0 z-[1000]', classes?.frame)}>
        <div className={tw('flex h-svh min-h-full items-center justify-center text-center', classes?.panelContainer)}>
          <DialogPrimitive.Content asChild forceMount>
            <MotionDiv
              className={tw(
                'w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl outline-none',
                classes?.panel,
                inter.className
              )}
              style={contentBackgroundColor ? { backgroundColor: contentBackgroundColor } : {}}
              {...(stableMotion
                ? {
                    initial: { opacity: 0, scale: 0.95 },
                    animate: { opacity: 1, scale: 1 },
                    exit: { opacity: 0, scale: 0.95 },
                    transition: { duration: 0.3, ease: 'easeOut' },
                  }
                : {})}
            >
              <VisuallyHidden asChild>
                <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
              </VisuallyHidden>
              {children}
            </MotionDiv>
          </DialogPrimitive.Content>
          {renderOutsideContent && (
            <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              {renderOutsideContent}
            </div>
          )}
        </div>
      </div>
    </DialogPrimitive.Portal>
  )

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {isOpen && renderContentWithAnimation()}
    </DialogPrimitive.Root>
  )
}

interface CloseButtonProps {
  onClose: () => void
}
const CloseButton = ({ onClose, className }: React.ComponentProps<'i'> & CloseButtonProps) => {
  return (
    <button data-tracking="ignore" onClick={onClose} className="flex items-center justify-center pl-[6px]">
      <SvgXmark aria-label="close" className={className} />
    </button>
  )
}

export const Modal = Object.assign(Root, {
  CloseButton,
})
